/** @format */

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { trackEvent } from "../utils/analytics";
import { Locale } from "../features/i18n/init";
import { useCallback, useEffect, useRef } from "react";

const SEARCH_DEBOUNCE_MS = 500;

type SearchKeyword = {
	keyword: string;
};

export interface SearchBarProps {
	shouldReset?: boolean;
	onPendingChange?: (isPending: boolean) => void;
	isSearching?: boolean;
}

export const SearchBar = ({ shouldReset, onPendingChange, isSearching }: SearchBarProps) => {
	const { t } = useTranslation();
	const { locale } = useParams<{ locale: Locale }>();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const { register, handleSubmit, reset, watch } = useForm<SearchKeyword>({
		mode: "onSubmit",
		defaultValues: { keyword: searchParams.get("q") || "" },
	});

	const keyword = watch("keyword");
	const debounceTimeoutRef = useRef<number | null>(null);

	useEffect(() => {
		if (shouldReset) {
			reset({ keyword: "" });
		}
	}, [shouldReset, reset]);

	const navigateWithQuery = useCallback(
		(query: string, options?: { replace?: boolean }) => {
			const params = new URLSearchParams(globalThis.location.search);
			if (query) {
				params.set("q", query);
			} else {
				params.delete("q");
			}
			params.delete("p");
			const search = params.toString();
			const url = `/${locale}/search${search ? `?${search}` : ""}`;
			if (options?.replace) {
				navigate(url, { replace: true });
			} else {
				navigate(url);
			}
		},
		[locale, navigate],
	);

	useEffect(() => {
		const currentQuery = new URLSearchParams(globalThis.location.search).get("q") || "";
		const isPending = keyword !== currentQuery;
		onPendingChange?.(isPending);
		if (!isPending) {
			return;
		}

		debounceTimeoutRef.current = window.setTimeout(() => {
			if (keyword) {
				trackEvent("search", { search_term: keyword });
			}

			navigateWithQuery(keyword, { replace: true });
		}, SEARCH_DEBOUNCE_MS);

		return () => {
			if (debounceTimeoutRef.current !== null) {
				window.clearTimeout(debounceTimeoutRef.current);
			}
		};
	}, [keyword, navigateWithQuery, onPendingChange]);

	const handleClear = () => {
		reset({ keyword: "" });
		navigateWithQuery("");
	};

	const onSubmit = (data: { keyword: string }) => {
		if (debounceTimeoutRef.current !== null) {
			window.clearTimeout(debounceTimeoutRef.current);
		}
		const currentQuery = new URLSearchParams(globalThis.location.search).get("q") || "";

		if (data.keyword !== currentQuery) {
			trackEvent("search", { search_term: data.keyword });
			navigateWithQuery(data.keyword);
		}
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)} className="w-full" data-testid="search-bar">
			<div className="flex flex-col w-full">
				<div className="relative w-full">
					<span
						aria-hidden="true"
						className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"
						data-testid={isSearching ? "search-bar-busy" : undefined}
					>
						{isSearching ? (
							<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="size-5 animate-spin">
								<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} />
								<path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
							</svg>
						) : (
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={1.5}
								stroke="currentColor"
								className="size-5"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
								/>
							</svg>
						)}
					</span>

					<input
						id="search-keyword"
						aria-label={t("searchBar.searchLabel")}
						placeholder={t("searchBar.searchPlaceholder")}
						className="border w-full rounded-[10px] h-10 pl-12 pr-28 items-center bg-white"
						{...register("keyword")}
					/>
					{keyword && (
						<button
							type="button"
							onClick={handleClear}
							aria-label={t("searchBar.clearSearch")}
							className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 transition-colors cursor-pointer"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								fill="none"
								viewBox="0 0 24 24"
								strokeWidth={2}
								stroke="currentColor"
								className="size-5"
							>
								<path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					)}
				</div>
			</div>
		</form>
	);
};
